import React, { useState, useEffect, useRef } from 'react';
import { DataGrid, Popup } from 'devextreme-react';
import Button from 'devextreme-react/button';
import TextBox from 'devextreme-react/text-box';
import { useTranslation } from 'react-i18next';
import notify from 'devextreme/ui/notify';
import { ColumnSetting } from '../../../helpers/columnSetting';
import '../page-setting/SettingsPopup.scss';
import {
  Column,
  DataGridRef,
  LoadPanel,
  SearchPanel,
  HeaderFilter,
  Scrolling,
  FilterPanel,
  FilterBuilderPopup,
  Toolbar,
  Item,
  Selection
} from "devextreme-react/cjs/data-grid";
import { confirm } from 'devextreme/ui/dialog';

// Stub functions - TODO: Implement with Supabase
const deleteSettingRequest = async (settingRef: number) => ({ resCode: 200, resMessage: 'Success' });
const setDefaultSettingRequest = async (settingRef: number) => ({ resCode: 200, resMessage: 'Success' });

export interface SavedSetting {
  settingRef: number;
  settingType: number;
  settingName: string;
  settingJson: string;
  settingStatus: number;
  isDefault?: number; // 1 ise default, 0 veya undefined değil
}

interface SettingsPopupProps {
  visible: boolean;
  onClose: () => void;
  // onSave fonksiyonuna opsiyonel olarak settingJson ekledik:
  onSave: (data: {
    settingName: string;
    settingRef: number;
    settingType: number;
    isDefault: number;
    settingJson?: string;
  }) => void;
  savedSettings: SavedSetting[];
  getCurrentGridColumns: (grid?: DataGridRef | null) => ColumnSetting[];
  onSelectSetting?: (setting: SavedSetting) => void;
}

const SettingsPopup: React.FC<SettingsPopupProps> = ({
  visible,
  onClose,
  onSave,
  savedSettings,
  getCurrentGridColumns,
  onSelectSetting,
}) => {
  const { t } = useTranslation();

  // Popup için ortak state'ler
  const [popupVisible, setPopupVisible] = useState<boolean>(false);
  const [popupMode, setPopupMode] = useState<'add' | 'edit'>('add');
  const [popupSettingName, setPopupSettingName] = useState<string>('');
  const gridRef = useRef<DataGridRef>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  // Listeden seçilen ayarı tutmak
  const [selectedSetting, setSelectedSetting] = useState<SavedSetting | null>(null);
  // Yerel liste yönetimi
  const [localSettings, setLocalSettings] = useState<SavedSetting[]>(savedSettings);

  // Düzenleme başlar başlamaz orijinal settingJson bilgisini kaydetmek için ref
  const originalSettingJsonRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    setLocalSettings(savedSettings);
    // Default ayarı kontrol ediyoruz:
    const defaultKeys = savedSettings
      .filter((s) => s.isDefault === 1)
      .map((s) => s.settingRef);
    setSelectedRowKeys(defaultKeys);
    if (defaultKeys.length > 0) {
      const defaultSetting = savedSettings.find(
        (s) => s.settingRef === defaultKeys[0]
      );
      setSelectedSetting(defaultSetting || null);
    }
  }, [savedSettings]);

  // Düzenleme moduna geçerken orijinal settingJson'u kaydediyoruz
  const handleEdit = (rowData: SavedSetting) => {
    setPopupMode("edit");
    setPopupSettingName(rowData.settingName);
    setSelectedSetting(rowData);
    // Düzenleme başladığında orijinal JSON'u kaydediyoruz:
    originalSettingJsonRef.current = rowData.settingJson;
    setPopupVisible(true);
  };

  const handleSavePopup = () => {
    if (!popupSettingName) {
      notify(t("enterAnOptionName"), "warning", 2000);
      return;
    }
    const refToSend = popupMode === "add" ? 0 : (selectedSetting?.settingRef ?? 0);
    const isDefaultValue = popupMode === "edit"
      ? (selectedSetting?.isDefault === 1 ? 1 : 0)
      : 0;

    onSave({
      settingName: popupSettingName,
      settingRef: refToSend,
      settingType: 1,
      isDefault: isDefaultValue,
      // Eğer ekleme modunda ise grid'den al, düzenleme modunda orijinal değeri kullan.
      settingJson: popupMode === "add"
        ? JSON.stringify(getCurrentGridColumns(gridRef.current?.instance()))
        : selectedSetting?.settingJson
    });
    setPopupSettingName('');
    setPopupVisible(false);
  };


  const handleMakeDefault = async () => {
    if (!selectedSetting) {
      notify(t("selectAnOption"), "warning", 2000);
      return;
    }
    try {
      const response = await setDefaultSettingRequest(selectedSetting.settingRef);
      if (response.resCode === 200) {
        notify(t("successfull"), "success", 2000);
        const updatedSettings = localSettings.map((s) => ({
          ...s,
          isDefault: s.settingRef === selectedSetting.settingRef ? 1 : 0,
        }));
        setLocalSettings(updatedSettings);
        onClose();
      } else {
        notify(t("anErrorOccured"), "error", 1500);
      }
    } catch (error) {
      notify(t("anErrorOccured"), "error", 2000);
    }
  };

  const handleSelectSetting = () => {
    if (!selectedSetting) {
      notify(t("selectAnOption"), "warning", 2000);
      return;
    }
    if (onSelectSetting) {
      onSelectSetting(selectedSetting);
    }
    onClose();
  };

  // Satır silme işlemi: onay alındıktan sonra gerçekleştirir.
  const handleDelete = async (rowData: SavedSetting) => {
    const result = await confirm(t("confirmDeleteMessage"), "Silme Onayı");
    if (result) {
      const settingRefToRemove = rowData.settingRef;
      return deleteSettingRequest(settingRefToRemove)
        .then((response) => {
          if (response.resCode === 200) {
            notify(t("successfull"), "success", 2000);
            setLocalSettings(localSettings.filter((s) => s.settingRef !== settingRefToRemove));
            if (selectedSetting?.settingRef === settingRefToRemove) {
              setSelectedSetting(null);
              setSelectedRowKeys([]);
            }
          } else {
            notify(response.resMessage, "error", 1500);
            throw new Error(response.resMessage);
          }
        })
        .catch((error) => {
          console.error("Error deleting product:", error);
          notify(t("anErrorOccured"), "error", 1500);
        });
    }
  };

  return (
    <>
      <Popup
        visible={visible}
        onHiding={onClose}
        width={"50%"}
        height={"50%"}
        dragEnabled={true}
        titleRender={() => (
          <div className="flex flex-row justify-between">
            <div className="flex flex-row">
              <Button icon="close" stylingMode="text" onClick={onClose} />
              <p className="ml-2 mt-1 justify-center font-bold text-sm">{t("settings")}</p>
            </div>
          </div>
        )}
        showCloseButton={true}
      >
        <DataGrid
          className="custom-grid compact-grid"
          ref={gridRef}
          dataSource={localSettings}
          keyExpr="settingRef"
          showBorders={true}
          noDataText={t("noData")}
          columnAutoWidth={false}
          allowColumnReordering
          allowColumnResizing
          height={"95%"}
          focusedRowEnabled={true}
          focusedRowKey={selectedSetting?.settingRef}
          selectedRowKeys={selectedRowKeys}
          onRowClick={(e) => {
            setSelectedRowKeys([e.data.settingRef]);
            setSelectedSetting(e.data);
          }}
          onSelectionChanged={(e) => {
            const keys = e.selectedRowKeys;
            if (keys.length > 1) {
              const lastKey = keys[keys.length - 1];
              setSelectedRowKeys([lastKey]);
              const lastSelected = e.selectedRowsData.find(
                (row) => row.settingRef === lastKey
              );
              setSelectedSetting(lastSelected || null);
            } else {
              setSelectedRowKeys(keys);
              setSelectedSetting(e.selectedRowsData[0] || null);
            }
          }}
        >
          <Selection mode="multiple" showCheckBoxesMode="always" />
          <LoadPanel showPane={false} />
          <SearchPanel placeholder={t("search")} searchVisibleColumnsOnly={false} visible={true} />
          <HeaderFilter visible />
          <Scrolling mode="virtual" />
          <LoadPanel showPane={true} />
          <FilterPanel visible={true} />
          <FilterBuilderPopup width={"50%"} height={"50%"} title={t("filterBuilder")} />
          <Toolbar>
            <Item name="searchPanel" location="before" locateInMenu="auto"/>
            <Item location="before" locateInMenu="auto">
              <Button icon="refresh" stylingMode="text" hint={t("refreshList")} />
            </Item>
            <Item location="before" locateInMenu="never">
              <div className="flex flex-row gap-x-2 justify-end">
                <Button
                  type="default"
                  stylingMode="contained"
                  icon="add"
                  onClick={() => {
                    setPopupMode("add");
                    setPopupSettingName("");
                    setPopupVisible(true);
                  }}
                  style={{ fontSize: "12px" }}
                  className="uniform-button"
                />
              </div>
            </Item>
            <Item location="after" locateInMenu="never">
              <div className="flex flex-row gap-x-1" style={{ flexWrap: 'wrap' }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Button
                    text={t("default")}
                    stylingMode="outlined"
                    onClick={handleMakeDefault}
                    disabled={!selectedSetting}
                    icon="check"
                    style={{ fontSize: "12px" }}
                    className="uniform-button"
                  />
                </div>
              </div>
            </Item>
            <Item location="after" locateInMenu="never">
              <div className="flex flex-row gap-x-1" style={{ flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    text={t("select")}
                    stylingMode="outlined"
                    onClick={handleSelectSetting}
                    disabled={!selectedSetting}
                    icon="fieldchooser"
                    style={{ fontSize: "12px" }}
                    className="uniform-button"
                  />
                  <div style={{ marginLeft: '10px' }}>
                    <Button
                      text={t("close")}
                      icon="close"
                      stylingMode="contained"
                      style={{ backgroundColor: "gray" }}
                      type="default"
                      onClick={onClose}
                    />
                  </div>
                </div>
              </div>
            </Item>
          </Toolbar>
          <Column dataField="settingName" caption={t("name")} allowEditing={false} />
          <Column dataField="comment" caption={t("comment")} alignment="left" allowEditing={false} />
          <Column
            alignment="right"
            width={"15%"}
            cellRender={({ data }) => (
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  width: '100%'
                }}
              >
                <Button
                  onClick={() => handleEdit(data)}
                  stylingMode="text"
                  style={{ fontSize: "8px", padding: 0 }}
                  icon="edit"
                />
                <Button
                  onClick={() => handleDelete(data)}
                  stylingMode="text"
                  style={{ fontSize: "8px", padding: 0 }}
                  icon="trash"
                />
              </div>
            )}
          />
        </DataGrid>
      </Popup>

      <Popup
        visible={popupVisible}
        onHiding={() => setPopupVisible(false)}
        titleRender={() => (
          <div className="flex flex-row justify-between">
            <div className="flex flex-row">
              <Button icon="close" stylingMode="text" onClick={() => setPopupVisible(false)} />
              <p className="ml-2 mt-1 justify-center font-bold text-sm">
                {popupMode === "add" ? t("add") : t("edit")}
              </p>
            </div>
          </div>
        )}
        width={450}
        height={200}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <TextBox
            value={popupSettingName}
            placeholder={t("name")}
            onValueChanged={(e: any) => setPopupSettingName(e.value)}
          />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button
              text={t("save")}
              type="default"
              stylingMode="contained"
              onClick={handleSavePopup}
            />
            <Button
              text={t("cancel")}
              stylingMode="outlined"
              onClick={() => setPopupVisible(false)}
            />
          </div>
        </div>
      </Popup>
    </>
  );
};

export default SettingsPopup;
